import {
  Box,
  Button,
  Flex,
  Input,
  Spacer,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { forwardRef, useContext, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WebAPIContext } from '../../contexts/WebAPIContext';
import { OrgViewAccessItemType, OrganizationWithHierarchyType } from '../../contexts/types';
import OrgChartSelectModal from './OrgChartSelectModal';

interface PendingAddItem {
  organizationId: string;
  memo: string | null;
}

export interface OrgViewAccessPanelHandle {
  save: () => Promise<boolean>;
}

interface OrgViewAccessPanelProps {
  userId: string;
  orgList: OrganizationWithHierarchyType[];
  committedOrgList: OrgViewAccessItemType[];
  visibleOrgIds: string[];
  isLoading: boolean;
}

/**
 * 組織閲覧許可（追加設定）パネル
 * - committedOrgList / isLoading は親（モーダルオープン時に取得済み）から受け取る
 * - 追加・削除・備考編集はローカル状態のみ更新。DB反映は親の保存ボタンから save() で行う
 */
const OrgViewAccessPanel = forwardRef<OrgViewAccessPanelHandle, OrgViewAccessPanelProps>(
  ({ userId, orgList, committedOrgList, visibleOrgIds, isLoading }, ref) => {
    const { t } = useTranslation();
    const WebAPI = useContext(WebAPIContext);
    const WebAPIRef = useRef(WebAPI);
    WebAPIRef.current = WebAPI;

    const [pendingAdd, setPendingAdd] = useState<PendingAddItem[]>([]);
    const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
    /** コミット済み行の備考変更 { organizationId: 新しい備考 } */
    const [pendingMemoUpdates, setPendingMemoUpdates] = useState<Record<string, string | null>>({});
    const [isOrgChartModalOpen, setIsOrgChartModalOpen] = useState(false);

    /** 表示用リスト = 保存済み（削除予定を除く）＋ 追加予定 */
    const displayList: OrgViewAccessItemType[] = [
      ...committedOrgList.filter(item => !pendingDeleteIds.includes(item.organizationId)),
      ...pendingAdd.map(p => ({
        userId,
        organizationId: p.organizationId,
        organizationName: orgList.find(o => o.organizationId === p.organizationId)?.organizationName ?? null,
        memo: p.memo,
        createUserId: null,
        createDate: null,
      })),
    ];

    const getMemoValue = (item: OrgViewAccessItemType): string => {
      if (pendingAdd.some(p => p.organizationId === item.organizationId)) {
        return pendingAdd.find(p => p.organizationId === item.organizationId)?.memo ?? '';
      }
      if (item.organizationId in pendingMemoUpdates) {
        return pendingMemoUpdates[item.organizationId] ?? '';
      }
      return item.memo ?? '';
    };

    useImperativeHandle(ref, () => ({
      save: async () => {
        try {
          for (const item of pendingAdd) {
            await WebAPIRef.current.request({
              query: `
                mutation putOrgViewAccess($input: OrgViewAccessInput!) {
                  putOrgViewAccess(input: $input) { status }
                }
              `,
              variables: {
                input: {
                  userId,
                  organizationId: item.organizationId,
                  memo: item.memo,
                },
              },
            });
          }
          for (const [orgId, memo] of Object.entries(pendingMemoUpdates)) {
            await WebAPIRef.current.request({
              query: `
                mutation putOrgViewAccess($input: OrgViewAccessInput!) {
                  putOrgViewAccess(input: $input) { status }
                }
              `,
              variables: {
                input: {
                  userId,
                  organizationId: orgId,
                  memo,
                },
              },
            });
          }
          for (const orgId of pendingDeleteIds) {
            await WebAPIRef.current.request({
              query: `
                mutation deleteOrgViewAccess($userId: String!, $organizationId: String!) {
                  deleteOrgViewAccess(userId: $userId, organizationId: $organizationId) { status }
                }
              `,
              variables: { userId, organizationId: orgId },
            });
          }
          return true;
        } catch (e) {
          console.error('OrgViewAccessPanel save error', e);
          return false;
        }
      },
    }), [pendingAdd, pendingMemoUpdates, pendingDeleteIds, userId]);

    const handleAdd = (organizationId: string) => {
      if (pendingDeleteIds.includes(organizationId)) {
        setPendingDeleteIds(prev => prev.filter(id => id !== organizationId));
        return;
      }
      const alreadyExists =
        committedOrgList.some(c => c.organizationId === organizationId) ||
        pendingAdd.some(p => p.organizationId === organizationId);
      if (alreadyExists) return;
      setPendingAdd(prev => [...prev, { organizationId, memo: null }]);
    };

    const handleDelete = (organizationId: string) => {
      if (pendingAdd.some(p => p.organizationId === organizationId)) {
        setPendingAdd(prev => prev.filter(p => p.organizationId !== organizationId));
      } else {
        setPendingDeleteIds(prev => [...prev, organizationId]);
        // 削除予定にしたら備考変更も破棄
        setPendingMemoUpdates(prev => {
          const next = { ...prev };
          delete next[organizationId];
          return next;
        });
      }
    };

    const handleToggle = (orgId: string) => {
      if (visibleOrgIds.includes(orgId)) return;
      const isAdded = displayList.some(d => d.organizationId === orgId);
      if (isAdded) {
        handleDelete(orgId);
      } else {
        handleAdd(orgId);
      }
    };

    const handleMemoChange = (organizationId: string, value: string) => {
      if (pendingAdd.some(p => p.organizationId === organizationId)) {
        setPendingAdd(prev =>
          prev.map(p => p.organizationId === organizationId ? { ...p, memo: value || null } : p)
        );
      } else {
        setPendingMemoUpdates(prev => ({ ...prev, [organizationId]: value || null }));
      }
    };

    const headerCellStyle = {
      fontWeight: 'bold',
      fontSize: '13px',
      whiteSpace: 'nowrap' as const,
      backgroundColor: 'gray.100',
      py: 2,
      px: 3,
    };

    const bodyCellStyle = {
      fontSize: '13px',
      py: 2,
      px: 3,
    };

    return (
      <Box border="1px" borderColor="gray.200" borderRadius="md" p={4} mt={4}>
        <Flex align="center" mb={3}>
          <Text fontWeight="bold">
            {t('features.MasterMaintenance.OrgViewAccessPanel.title')}
          </Text>
          <Spacer />
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => setIsOrgChartModalOpen(true)}
          >
            {t('features.MasterMaintenance.OrgViewAccessPanel.button.openOrgChart')}
          </Button>
        </Flex>

        {isLoading ? (
          <Flex justify="center" py={4}>
            <Spinner size="sm" />
          </Flex>
        ) : (
          <Box maxH="180px" overflowY="auto" mb={4}>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th {...headerCellStyle}>{t('features.MasterMaintenance.OrgViewAccessPanel.field.organizationName')}</Th>
                  <Th {...headerCellStyle}>{t('features.MasterMaintenance.OrgViewAccessPanel.field.memo')}</Th>
                  <Th {...headerCellStyle} w="80px" />
                </Tr>
              </Thead>
              <Tbody>
                {displayList.length === 0 ? (
                  <Tr>
                    <Td {...bodyCellStyle} colSpan={3} textAlign="center" color="gray.400">
                      {t('common.label.noData', { defaultValue: '（なし）' })}
                    </Td>
                  </Tr>
                ) : (
                  displayList.map((item) => (
                    <Tr key={item.organizationId}>
                      <Td {...bodyCellStyle}>{item.organizationName ?? item.organizationId}</Td>
                      <Td {...bodyCellStyle}>
                        <Input
                          size="xs"
                          value={getMemoValue(item)}
                          onChange={e => handleMemoChange(item.organizationId, e.target.value)}
                          placeholder={t('features.MasterMaintenance.OrgViewAccessPanel.placeholder.memo')}
                          borderRadius="md"
                        />
                      </Td>
                      <Td {...bodyCellStyle}>
                        <Button
                          size="xs"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => handleDelete(item.organizationId)}
                          isDisabled={visibleOrgIds.includes(item.organizationId)}
                        >
                          {t('features.MasterMaintenance.OrgViewAccessPanel.button.delete')}
                        </Button>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        )}

        <OrgChartSelectModal
          isOpen={isOrgChartModalOpen}
          onClose={() => setIsOrgChartModalOpen(false)}
          orgList={orgList}
          addedIds={displayList.map(d => d.organizationId)}
          onToggle={handleToggle}
          disabledIds={visibleOrgIds}
        />
      </Box>
    );
  }
);

OrgViewAccessPanel.displayName = 'OrgViewAccessPanel';
export default OrgViewAccessPanel;
